import { useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import ApplicationsFilter, {
  ApplicationFilterState,
} from "../../components/ApplicationsFilter/ApplicationsFilter";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";
import { Dialog } from "../../components/CommonComponents/Dialog/Dialog";
import { ViewButton } from "../../components/CommonComponents/EditComponent/EditComponent";
import { getApplicationTypeColumnLabel } from "../../config/phaseConstants";
import Paginator, {
  PaginatorForRDT,
} from "../../components/CommonComponents/Paginator/Paginator";
import SortIcon from "../../components/CommonComponents/SortIcon/SortIcon";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { isLevel1 } from "../../hoc/isLevel1";
import { TApplication } from "../../hooks/applications.hooks";
import { useDebounce, useToggle } from "../../hooks/common";
import {
  useApplicationCountForLevel1,
  useApplicationsForLevel1,
} from "../../hooks/level-1-application-approval.hooks";
import { formatDate } from "../../utils/date";
import { validLevelOneStatuses } from "./LevelOne.constants";
import ApplicationListViewToggle from "../../components/ApplicationListViewToggle/ApplicationListViewToggle";
import {
  ApplicationListView,
  DEFAULT_APPLICATION_LIST_VIEW,
} from "../../config/pendingApplicationView";

export const LevelOneApplications = isLevel1(() => {
  const navigate = useNavigate();
  const [filterOpen, toggleFilter] = useToggle();
  const [filterState, setFilterState] = useState<ApplicationFilterState>({
    connector: "and",
    conditions: [],
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [view, setView] = useState<"table" | "card">("table");
  const [page, setPage] = useState(1);
  /**
   * The list opens on the applications waiting for Level 1; "Show All
   * Applications" switches to the full list. Changing it goes back to page 1,
   * since the other view may have fewer pages.
   */
  const [listView, setListView] = useState<ApplicationListView>(DEFAULT_APPLICATION_LIST_VIEW);
  const changeListView = (next: ApplicationListView) => {
    setListView(next);
    setPage(1);
  };
  const search = useDebounce(searchQuery);
  const [{ sortKey, sortOrder, userHasSorted }, setSort] = useState({
    sortKey: "submittedAt",
    sortOrder: -1,
    userHasSorted: false,
  });
  const { isLoading: isLoading1, data: counts } = useApplicationCountForLevel1({
    search,
    filters: filterState,
    view: listView,
  });
  const { isLoading: isLoading2, data } = useApplicationsForLevel1({
    page,
    search,
    limit: 10,
    //sort: sortKey;
    sort: userHasSorted ? sortKey : "__default__",
    sortVal: sortOrder,
    filters: filterState,
    view: listView,
  });

  const applications: TApplication[] = data?.data ?? [];

  // 🔽 ADD THIS CONSOLE LOG
  console.log(
      "Applications (Editable vs Non-Editable):",
      applications.map((app) => ({
        applicationId: app.applicationId,
        status: app.status,
        submittedAt: app.submittedAt,
        isEditable: app.isActionAllowed, // true = editable, false = not editable
      }))
  );

  console.table(
      applications.map((app) => ({
        ID: app.applicationId,
        Status: app.status,
        Submitted: app.submittedAt,
        Editable: app.isActionAllowed ? "YES" : "NO",
      }))
  );

  const oldestPendingL1Id = useMemo(() => {
    if (!applications.length) return null;

    const pending = applications
      .filter(
        (d) => d.status === "Pending Level 1 Review" && d.submittedAt
      )
      .sort(
        (a, b) =>
          new Date(a.submittedAt!).getTime() -
          new Date(b.submittedAt!).getTime()
      );

    return pending.length ? pending[0].id : null;
  }, [applications]);

  /*
  const canViewApplication = (row: TApplication) => {
    // Completed → keep existing behavior
    if (row.status === "Completed") return true;

    // Only the oldest Pending Level 1 Review is allowed
    if (
      row.status === "Pending Level 1 Review" &&
      row.id === oldestPendingL1Id
    ) {
      return true;
    }

    // Everything else → disabled
    return false;
  };*/

  // Replace entire canViewApplication function
  const canViewApplication = (row: TApplication) => !!row.isActionAllowed;

  /**
   * Every row can be opened. A row L1 can act on goes to its action page (the
   * payment verification page for Pending Level 1 Final Review, otherwise the
   * L1 review page); every other row opens the read-only view page. The table
   * link, the Action button and both card buttons all route through here so
   * they cannot drift apart again.
   */
  const openApplication = (row: TApplication) => {
    if (!canViewApplication(row)) {
      navigate("/view-application-details", { state: { id: row.id } });
      return;
    }

    if (row.status === "Pending Level 1 Final Review") {
      navigate("/level-1-payment-verify-application", {
        state: { id: row.id },
      });
    } else {
      const isL1Route = ["Pending Level 1 Review", "Returned By Level 1 Reviewer"].includes(row.status);
      navigate(
        isL1Route ? "/level-1-application-details" : "/view-application-details",
        { state: { id: row.id } }
      );
    }
  };

  const sortedData = useMemo(() => {
    if (!applications.length) return [];

    return [...applications].sort((a, b) => {
      const getPriority = (status: string) => {
        if (status === "Pending Level 1 Review") return 1;
        if (status === "Pending Level 1 Final Review") return 2;
        return 3;
      };

      const priorityA = getPriority(a.status);
      const priorityB = getPriority(b.status);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

        /**
         * Oldest submission first, matching the server's default order, so the
         * single actionable Pending Level 1 Review row stays at the top of the page.
         */
        // return (
        //   new Date(b.submittedAt || 0).getTime() -
        //   new Date(a.submittedAt || 0).getTime()
        // );
        return (
          new Date(a.submittedAt || 0).getTime() -
          new Date(b.submittedAt || 0).getTime()
        );
    });
  }, [applications]);

  const columns = useMemo(
    (): TableColumn<TApplication & { serviceName?: string }>[] => [
      {
        name: "Application ID",
        sortable: true,
        sortField: "applicationId",
        selector: (d) => d.applicationId,
        center: true,
        width: "180px",
        cell: (row) => {
          const enabled = canViewApplication(row);

          /**
           * BEFORE: non-actionable rows were disabled and did nothing on click.
           */
          // <button
          //   disabled={!enabled}
          //   onClick={() => {
          //     if (!enabled) return;
          //     // UPDATED: Route all valid Level 1 statuses to appropriate Level 1 routes
          //     if (row.status === "Pending Level 1 Final Review") {
          //       navigate("/level-1-payment-verify-application", {
          //         state: { id: row.id }, });
          //     } else {
          //       const isL1Route = ["Pending Level 1 Review", "Returned By Level 1 Reviewer"].includes(row.status);
          //       navigate(
          //         isL1Route ? "/level-1-application-details" : "/view-application-details",
          //         { state: { id: row.id } }
          //       );
          //     }
          //   }}
          //   className={`font-semibold ${enabled
          //     ? "text-primary underline cursor-pointer"
          //     : "text-gray-400 cursor-not-allowed"
          //     }`}
          // >
          return (
            <button
              onClick={() => openApplication(row)}
              className={`font-semibold underline cursor-pointer ${enabled
                ? "text-primary"
                : "text-gray-400"
                }`}
            >
              {row.applicationId}
            </button>
          );
        },
      },
      {
        name: "Plot Owner's Name",
        sortable: true,
        sortField: "plot_owner_s_name",
        selector: (d) => d.plot_owner_s_name || "-",
        cell: (d) => (
          <span className="text-center">{d.plot_owner_s_name || "-"}</span>
        ),
        center: true,
        grow: 2,
      },
      {
        name: "Name of Service",
        selector: (d) => d.serviceName || "-",
        cell: (d) => (
          <span className="text-center">{d.serviceName || "-"}</span>
        ),
        width: "250px",
        sortable: true,
        sortField: "name_of_service",
        center: true,
      },
      {
        name: "Application Type",
        selector: (d) => getApplicationTypeColumnLabel(d),
        center: true,
        width: "180px",
      },
      {
        name: "Status",
        center: true,
        sortable: true,
        sortField: "status",
        width: "160px",
        cell: (d) => <span className="text-center">{d.status}</span>,
      },
      {
        name: "Days Left for Action",
        // sortable: true,
        // sortField: "daysLeftForAction",
        width: "150px",
        cell: (d) => {
          console.log(`Rendering days left for ${d.applicationId}:`, d.daysLeftForAction);

          // If no value, show "-"
          if (d.daysLeftForAction === null || d.daysLeftForAction === undefined) {
            return <span className="text-center">-</span>;
          }

          // If days left is 0, display with urgent styling
          if (d.daysLeftForAction === 0) {
            return <span className="text-center text-red-600 font-bold">0</span>;
          }

          // Normal case with days remaining
          const isDanger = d.daysLeftForAction <= 2; // Less than or equal to 2 days is urgent
          return (
            <span className={`text-center ${isDanger ? 'text-red-600 font-bold' : ''}`}>
              {d.daysLeftForAction}
            </span>
          );
        },
        center: true,
      },
      {
        name: "Date of Submission",
        width: "220px",
        center: true,
        sortable: true,
        sortField: "submittedAt",
        cell: (d) => (d.submittedAt ? formatDate(d.submittedAt) : "-"),
      },
      {
        name: "Created By",
        selector: (row: any) => row.createdBy,
        cell: (row: any) => (
          <span className="text-center">{row.createdBy}</span>
        ),
        center: true,
        sortable: true,
        sortField: "createdBy",
        width: "200px",
      },
      {
        name: "Created On",
        selector: (row: any) => formatDate(new Date(row.createdAt)),
        center: true,
        sortable: true,
        sortField: "createdAt",
        width: "170px",
      },
      {
        name: "Modified By",
        selector: (row: any) => row.modifiedBy,
        cell: (row: any) => (
          <span className="text-center">{row.modifiedBy}</span>
        ),
        center: true,
        sortable: true,

        sortField: "modifiedBy",
        width: "200px",
      },
      {
        name: "Modified On",
        selector: (row: any) => formatDate(new Date(row.updatedAt)),
        center: true,
        sortable: true,
        sortField: "updatedAt",
        width: "170px",
      },
      {
        name: "Action",
        center: true,
        width: "150px",
        cell: (e) => {
          /** No longer read: the button is always clickable. */
          // const enabled = canViewApplication(e);

          return (
            <div className="flex justify-center">
              {/**
                * BEFORE: disabled for non-actionable rows. Now always clickable.
                *
                * <ViewButton
                *   disabled={!enabled}
                *   onClick={() => {
                *     if (!enabled) return;
                *     // UPDATED: Route all valid Level 1 statuses to appropriate Level 1 routes
                *     if (e.status === "Pending Level 1 Final Review") {
                *       navigate("/level-1-payment-verify-application", {
                *         state: { id: e.id }, });
                *     } else {
                *       const isL1Route = ["Pending Level 1 Review", "Returned By Level 1 Reviewer"].includes(e.status);
                *       navigate(
                *         isL1Route ? "/level-1-application-details" : "/view-application-details",
                *         { state: { id: e.id } }
                *       );
                *     }
                *   }}
                * />
                */}
              <ViewButton onClick={() => openApplication(e)} />
            </div>
          );
        },
      },
    ],
    // [navigate, canViewApplication, oldestPendingL1Id]
    [navigate, canViewApplication, openApplication, oldestPendingL1Id]
  );

  return (
    // <div className="p-8">
    //   <div className="flex justify-between mb-4">
    <div className="p-4 md:p-8">
      <div className="flex justify-between items-center flex-wrap lg:flex-nowrap gap-4 mb-4">
        {/* <h4 className="text-2xl font-bold">All Applications</h4> */}
        <h4 className="text-2xl font-bold">
          {listView === "pending" ? "Pending Applications" : "All Applications"}
        </h4>
        {/* <div className="flex space-x-4"> */}
        <div className="flex gap-4 grow lg:grow-0 flex-wrap md:flex-nowrap">
          <ApplicationListViewToggle view={listView} onChange={changeListView} />
          <button
            className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center"
            onClick={() => setView((e) => (e === "card" ? "table" : "card"))}
          >
            {view === "table" ? (
              <i className="fa fa-th text-primary m-0" />
            ) : (
              <i className="fa fa-list-ul text-primary m-0" />
            )}
          </button>
          <button
            className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center min-w-[42px]"
            onClick={toggleFilter}
          >
            <i className="fa fa-filter text-primary m-0" />
          </button>
          {/* <div className="flex rounded-xl border border-[#968f8f] bg-white items-stretch overflow-hidden"> */}
          <div className="flex rounded-xl border grow lg:grow-0 border-[#968f8f] bg-white items-stretch overflow-hidden">
            <input
              type="text"
              placeholder="Search..."
              className="bg-none px-3"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <div className="p-3 shadow-md flex justify-center items-center">
              <i className="fa fa-search text-primary m-0" />
            </div>
          </div>
        </div>
      </div>
      {view === "table" ? (
        <BoxComponent>
          <DataTable
            pagination
            sortServer
            paginationServer
            className="rounded-0 min-w-full"
            columns={columns}
            data={sortedData}
            sortIcon={<SortIcon />}
            customStyles={tableCustomStyles}
            onChangePage={(p) => setPage(p)}
            paginationTotalRows={counts?.data || 0}
            progressPending={isLoading1 || isLoading2}
            // onSort={(col, dir) => {
            //   if (!col?.sortField) return;
            //   setSort({
            //     sortKey: col.sortField || "",
            //     sortOrder: dir === "asc" ? 1 : -1,
            //   });
            // }}
            onSort={(col, dir) => {
              if (!col?.sortField) return;
              setSort({
                sortKey: col.sortField,
                sortOrder: dir === "asc" ? 1 : -1,
                userHasSorted: true
              });
            }}
            paginationComponent={PaginatorForRDT}
          />
        </BoxComponent>
      ) : (
        <>
          <BoxComponent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {sortedData.map((e) => {
                const enabled = canViewApplication(e);
                return (
                  <div
                    // className="p-4 rounded-2xl bg-white shadow-lg border border-gray-300"
                    className="p-4 rounded-2xl bg-white flex flex-col shadow-lg border border-gray-300"
                    key={e.id}
                  >
                    <div className="flex flex-col h-full">
                      <div className="flex-grow">
                        {/* <div className="flex justify-between items-center mb-4"> */}
                        <div className="flex justify-between items-start space-x-4 mb-4">
                          <div className="text-center bg-yellow-500 text-grey-600 inline-block py-1 px-4 rounded-full text-base text-[12px] font-semibold">
                            {e.status}
                          </div>
                          {/* <button
                          onClick={() => {
                            navigate(`/level-1-application-details`, {
                              state: { id: e?.id },
                            });
                          }}
                          className="icon-button"
                        >
                          <i className="fa fa-eye ml-2"></i>
                        </button> */}
                          <div className="flex space-x-2">
                            {/**
                              * BEFORE: disabled for non-actionable rows. Now always
                              * clickable.
                              *
                              * <ViewButton
                              *   disabled={!enabled}
                              *   onClick={() => {
                              *     if (!enabled) return;
                              *     if (e.status === "Pending Level 1 Final Review") {
                              *       navigate("/level-1-payment-verify-application", {
                              *         state: { id: e.id }, });
                              *     } else {
                              *       navigate(
                              *         e.status === "Pending Level 1 Review"
                              *           ? "/level-1-application-details"
                              *           : "/view-application-details",
                              *         { state: { id: e.id } }
                              *       );
                              *     }
                              *   }}
                              * />
                              */}
                            <ViewButton onClick={() => openApplication(e)} />
                          </div>
                        </div>
                        <div className="mb-2 flex">
                          <p className="font-bold text-base w-1/2">
                            Application ID
                          </p>
                          <p className="text-base mx-2">:</p>
                          {/**
                            * BEFORE: disabled for non-actionable rows, and sent every
                            * enabled row except Pending Level 1 Review to the view
                            * page, so an actionable Pending Level 1 Final Review card
                            * opened read-only. Now matches the table link.
                            *
                            * <p className="text-base w-2/3"><button
                            *   disabled={!enabled}
                            *   onClick={() => {
                            *     if (!enabled) return;
                            *     navigate(
                            *       e.status === "Pending Level 1 Review"
                            *         ? "/level-1-application-details"
                            *         : "/view-application-details",
                            *       { state: { id: e.id } }
                            *     );
                            *   }}
                            *   className={`text-base w-2/3 text-left font-semibold ${enabled
                            *     ? "text-primary underline cursor-pointer"
                            *     : "text-gray-400 cursor-not-allowed"
                            *     }`}
                            * >
                            */}
                          <p className="text-base w-2/3"><button
                            onClick={() => openApplication(e)}
                            className={`text-base w-2/3 text-left font-semibold underline cursor-pointer ${enabled
                              ? "text-primary"
                              : "text-gray-400"
                              }`}
                          >
                            {e.applicationId}
                          </button></p>
                        </div>
                        <div className="mb-2 flex">
                          <p className="font-bold text-base w-1/2">
                            Plot Owner's Name
                          </p>
                          <p className="text-base mx-2">:</p>
                          <p className="text-base w-2/3">
                            {e.plot_owner_s_name || "-"}
                          </p>
                        </div>
                        <div className="mb-2 flex">
                          <p className="font-bold text-base w-1/2">
                            Date of Submission
                          </p>
                          <p className="text-base mx-2">:</p>
                          <p className="text-base w-2/3">
                            {formatDate(e.submittedAt || "x")}
                          </p>
                        </div>
                        <div className="mb-2 flex">
                          <p className="font-bold text-base w-1/2">Days Left for Action</p>
                          <p className="text-base mx-2">:</p>
                          {e.daysLeftForAction === null || e.daysLeftForAction === undefined ? (
                            <p className="text-base w-2/3">-</p>
                          ) : e.daysLeftForAction === 0 ? (
                            <p className="text-base w-2/3 text-red-600 font-bold">0</p>
                          ) : (
                            <p className={`text-base w-2/3 ${e.daysLeftForAction <= 2 ? 'text-red-600 font-bold' : ''}`}>
                              {e.daysLeftForAction}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            <Paginator
              limit={10}
              page={page}
              setPage={setPage}
              count={counts?.data || 0}
            />
          </BoxComponent>
        </>
      )}
      <Dialog
        onClose={toggleFilter}
        open={filterOpen}
        className="flex flex-col sm:w-[600px] lg:w-[800px]"
      >
        <ApplicationsFilter
          onClose={toggleFilter}
          defaultFilter={filterState}
          onChange={setFilterState}
        />
      </Dialog>
    </div>
  );
});

